import React, { useCallback, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { expVal } from '@confluence/feature-experiments';

import Form from '@atlaskit/form';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Stack, Text, xcss } from '@atlaskit/primitives';
import SectionMessage from '@atlaskit/section-message';

import { fg } from '@confluence/feature-gating';

import {
	useAiBulkNavigationCurrentPage,
	useAiBulkNavigationGotoPage,
} from '../../providers/AiBulkNavigationContextProvider.tsx';
import {
	useBulkCreateContext,
	useGetIsMissingColumnMappingError,
} from '../../providers/BulkCreateContextProvider.tsx';
import { messages } from '../../messages.ts';
import { SidePanelWrapper } from '../work-create-common/SidePanelWrapper.tsx';
import {
	useAvailableSitesState,
	useIsConfigureLoading,
	useIssueTypesState,
	useMappingsActions,
	useProjectsActions,
	useProjectsState,
	useResetConfigureState,
	useAvailableSitesActions,
	useIssueTypesActions,
} from '../../providers/BulkConfigureContextProvider.tsx';
import { saveJiraConfigDetailsToLocalStorage } from '../../utils/localStorageHelpers.ts';
import { BulkCreateNavigationHeader } from '../bulk-work-create-common/BulkCreateNavigationHeader.tsx';
import {
	SiteSelector,
	IssueTypeSelector as DefaultIssueTypeSelector,
	ProjectSelector,
} from '../work-create-form-common/index.tsx';

import { ColumnToIssueFieldMappingView } from './ColumnToIssueFieldMappingView.tsx';
import { BulkConfigureFooter } from './BulkConfigureFooter.tsx';

export type MappingViewRefType = {
	validateMappings: () => boolean;
};

const bulkConfigureViewStyles = xcss({
	flexGrow: 1,
	overflowY: 'auto',
});

const ATLAS_KIT_SELECT_OPEN_CLASS = '[class*="select__menu"]';

// This is the view for both AI and non-AI bulk create where users can edit and create individual issues
export const BulkConfigureView = ({ closePanel = () => {} }: { closePanel?: () => void }) => {
	const [
		{
			isCreatedWithAi,
			isCreatedFromTable,
			currentJiraSite,
			currentJiraProject,
			currentDefaultIssueType,
		},
		{ setCurrentJiraSite, setCurrentJiraProject, setCurrentDefaultIssueType },
	] = useBulkCreateContext();
	const { formatMessage } = useIntl();
	const { optionsAvailableSites, selectedAvailableSite } = useAvailableSitesState();
	const { setSelectedAvailableSite } = useAvailableSitesActions();
	const { selectedProject, optionsRecentProjects, optionsAllProjects } = useProjectsState();
	const { setSelectedProject, setProjectSelectorQuery } = useProjectsActions();
	const { selectedIssueType, optionsIssueTypes } = useIssueTypesState();
	const { setSelectedIssueType } = useIssueTypesActions();
	const { isLoading, isJiraError } = useIsConfigureLoading();
	const resetConfigureState = useResetConfigureState();
	const { setIsJiraError } = useProjectsActions();
	const {
		setUnsavedColumnToFieldMappings,
		initUnsavedColumnToFieldMappings,
		saveUnsavedColumnToFieldMapping,
	} = useMappingsActions();

	const goToPage = useAiBulkNavigationGotoPage();
	const currentPage = useAiBulkNavigationCurrentPage();

	const isOpen = currentPage === 'configure';
	const isMissingColumnMappingError = useGetIsMissingColumnMappingError();

	const mappingViewRef = useRef<MappingViewRefType | null>(null);
	const scrollableContainerRef = useRef<HTMLDivElement | null>(null);

	const handleAutoScrollOnMenuOpen = useCallback(() => {
		if (scrollableContainerRef.current) {
			// Add a delay to allow the dropdown to render
			setTimeout(() => {
				const container = scrollableContainerRef.current;
				if (!container) return;

				// Find any open dropdown menu
				const openDropdown = container.querySelector(ATLAS_KIT_SELECT_OPEN_CLASS);

				if (openDropdown) {
					const containerRect = container.getBoundingClientRect();
					const dropdownRect = openDropdown.getBoundingClientRect();
					const dropdownBottom = dropdownRect.bottom;
					const containerBottom = containerRect.bottom;

					// If the dropdown extends beyond the container's visible area, scroll to show it
					if (dropdownBottom > containerBottom) {
						const scrollAmount = dropdownBottom - containerBottom;
						container.scrollTo({
							top: container.scrollTop + scrollAmount,
							behavior: 'smooth',
						});
					}
				} else {
					// Called e.g. when the user clicks the "Add mapping" button (and therefore there is no open dropdown)
					const currentHeight = container.scrollHeight;
					const visibleHeight = container.clientHeight;
					const currentScroll = container.scrollTop;

					// Only scroll if content extends beyond current view
					if (currentHeight > visibleHeight + currentScroll) {
						container.scrollTo({
							top: currentHeight - visibleHeight,
							behavior: 'smooth',
						});
					}
				}
			}, 0); // Ensure this runs after the dropdown is rendered
		}
	}, []);

	const onSubmit = useCallback(() => {
		if (!isCreatedWithAi) {
			if (mappingViewRef.current?.validateMappings()) {
				saveUnsavedColumnToFieldMapping();
			} else if (isCreatedFromTable) {
				return;
			}
		}
		selectedAvailableSite && void setCurrentJiraSite(selectedAvailableSite);
		selectedProject && void setCurrentJiraProject(selectedProject);
		selectedIssueType && void setCurrentDefaultIssueType(selectedIssueType);
		saveJiraConfigDetailsToLocalStorage({
			selectedSite: selectedAvailableSite,
			selectedProject,
			selectedIssueType,
		});
		void goToPage('default');
	}, [
		isCreatedWithAi,
		selectedAvailableSite,
		setCurrentJiraSite,
		selectedProject,
		setCurrentJiraProject,
		selectedIssueType,
		setCurrentDefaultIssueType,
		goToPage,
		isCreatedFromTable,
		saveUnsavedColumnToFieldMapping,
	]);

	const onClose = useCallback(() => {
		if (currentJiraSite && currentJiraProject && currentDefaultIssueType) {
			void resetConfigureState(currentJiraSite, currentJiraProject, currentDefaultIssueType);
		}
		if (!isCreatedWithAi) {
			setUnsavedColumnToFieldMappings(null);
			initUnsavedColumnToFieldMappings();
		}
		void setIsJiraError(false);
		void goToPage('default');
	}, [
		currentDefaultIssueType,
		currentJiraProject,
		currentJiraSite,
		goToPage,
		resetConfigureState,
		setIsJiraError,
		isCreatedWithAi,
		setUnsavedColumnToFieldMappings,
		initUnsavedColumnToFieldMappings,
	]);

	const isSaveDisabled =
		isLoading || !selectedAvailableSite || !selectedProject || !selectedIssueType;
	const bulkCreateConfigureBodyText = fg('confluence-issue-terminology-refresh')
		? messages.bulkCreateConfigureBodyTextIssueTermRefresh
		: messages.bulkCreateConfigureBodyText;
	return (
		<SidePanelWrapper isOpen={isOpen}>
			<Box padding="space.200" xcss={bulkConfigureViewStyles} ref={scrollableContainerRef}>
				<Stack space="space.100">
					<BulkCreateNavigationHeader onClose={onClose}>
						<FormattedMessage {...messages.bulkConfigureHeaderText} />
					</BulkCreateNavigationHeader>
					<Text color="color.text.subtlest">
						<FormattedMessage {...bulkCreateConfigureBodyText} />
					</Text>
					{isJiraError && (
						<SectionMessage appearance="error" testId="bulkCreateErrorSectionMessage">
							<FormattedMessage
								{...(expVal('project-terminology-refresh', 'isEnabled', false)
									? messages.bulkConfigureJiraErrorTextGalaxia
									: messages.bulkConfigureJiraErrorText)}
							/>
						</SectionMessage>
					)}
					<Stack space="space.300">
						{fg('platform-design_system_team-form_conversion') ? (
							<Form onSubmit={onSubmit} id="configureForm">
								{fg('jira_ai_decouple_issue_create_fields') ? (
									<>
										<SiteSelector
											options={optionsAvailableSites ?? []}
											value={selectedAvailableSite}
											onChange={(site) => site && setSelectedAvailableSite(site)}
											isLoading={isLoading}
											isDisabled={isLoading}
											label={formatMessage(messages.bulkConfigureFormSite)}
										/>
										<ProjectSelector
											optionsAllProjects={optionsAllProjects ?? []}
											optionsRecentProjects={optionsRecentProjects ?? []}
											value={selectedProject}
											onChange={(project) => project && setSelectedProject(project)}
											isLoading={isLoading}
											isDisabled={isLoading}
											onInputChange={(query) => setProjectSelectorQuery(query)}
											label={formatMessage(
												expVal('project-terminology-refresh', 'isEnabled', false)
													? messages.bulkConfigureFormProjectGalaxia
													: messages.bulkConfigureFormProject,
											)}
											labelProjectSelectorRecentProjects={formatMessage(
												expVal('project-terminology-refresh', 'isEnabled', false)
													? messages.bulkConfigureFormProjectSelectorRecentProjectsGalaxia
													: messages.bulkConfigureFormProjectSelectorRecentProjects,
											)}
											labelProjectSelectorProjects={formatMessage(
												expVal('project-terminology-refresh', 'isEnabled', false)
													? messages.bulkConfigureFormProjectSelectorProjectsGalaxia
													: messages.bulkConfigureFormProjectSelectorProjects,
											)}
										/>
										<DefaultIssueTypeSelector
											optionsIssueTypes={optionsIssueTypes ?? []}
											value={selectedIssueType}
											onChange={(issueType) => issueType && setSelectedIssueType(issueType)}
											isLoading={isLoading}
											isDisabled={isLoading}
											label={formatMessage(
												fg('confluence-issue-terminology-refresh')
													? messages.bulkConfigureFormDefaultIssueTypeIssueTermRefresh
													: messages.bulkConfigureFormDefaultIssueType,
											)}
										/>
									</>
								) : (
									<>
										<SiteSelector />
										<ProjectSelector />
										<DefaultIssueTypeSelector />
									</>
								)}
							</Form>
						) : (
							<Form onSubmit={onSubmit}>
								{({ formProps }) => (
									<form id="configureForm" {...formProps}>
										{fg('jira_ai_decouple_issue_create_fields') ? (
											<>
												<SiteSelector
													options={optionsAvailableSites ?? []}
													value={selectedAvailableSite}
													onChange={(site) => site && setSelectedAvailableSite(site)}
													isLoading={isLoading}
													isDisabled={isLoading}
													label={formatMessage(messages.bulkConfigureFormSite)}
												/>
												<ProjectSelector
													optionsAllProjects={optionsAllProjects ?? []}
													optionsRecentProjects={optionsRecentProjects ?? []}
													value={selectedProject}
													onChange={(project) => project && setSelectedProject(project)}
													isLoading={isLoading}
													isDisabled={isLoading}
													onInputChange={(query) => setProjectSelectorQuery(query)}
													label={formatMessage(
														expVal('project-terminology-refresh', 'isEnabled', false)
															? messages.bulkConfigureFormProjectGalaxia
															: messages.bulkConfigureFormProject,
													)}
													labelProjectSelectorRecentProjects={formatMessage(
														expVal('project-terminology-refresh', 'isEnabled', false)
															? messages.bulkConfigureFormProjectSelectorRecentProjectsGalaxia
															: messages.bulkConfigureFormProjectSelectorRecentProjects,
													)}
													labelProjectSelectorProjects={formatMessage(
														expVal('project-terminology-refresh', 'isEnabled', false)
															? messages.bulkConfigureFormProjectSelectorProjectsGalaxia
															: messages.bulkConfigureFormProjectSelectorProjects,
													)}
												/>
												<DefaultIssueTypeSelector
													optionsIssueTypes={optionsIssueTypes ?? []}
													value={selectedIssueType}
													onChange={(issueType) => issueType && setSelectedIssueType(issueType)}
													isLoading={isLoading}
													isDisabled={isLoading}
													label={formatMessage(
														fg('confluence-issue-terminology-refresh')
															? messages.bulkConfigureFormDefaultIssueTypeIssueTermRefresh
															: messages.bulkConfigureFormDefaultIssueType,
													)}
												/>
											</>
										) : (
											<>
												<SiteSelector />
												<ProjectSelector />
												<DefaultIssueTypeSelector />
											</>
										)}
									</form>
								)}
							</Form>
						)}
						{isMissingColumnMappingError && (
							<SectionMessage
								appearance="information"
								title={
									messages.bulkConfigureMissingTableCoulumnMappingErrorHeaderText.defaultMessage
								}
								testId="bulkCreateWarningSectionMessage"
							>
								<FormattedMessage {...messages.bulkConfigureMissingTableCoulumnMappingErrorText} />
							</SectionMessage>
						)}
						{!isCreatedWithAi &&
							(isCreatedFromTable || !fg('confluence-create-work-items-from-action-items')) && (
								<ColumnToIssueFieldMappingView
									ref={mappingViewRef}
									onAutoScrollOnMenuOpen={handleAutoScrollOnMenuOpen}
								/>
							)}
					</Stack>
				</Stack>
			</Box>
			<BulkConfigureFooter
				onClose={onClose}
				closePanel={closePanel}
				isSaveDisabled={isSaveDisabled}
			/>
		</SidePanelWrapper>
	);
};
